/**
 * Visible legend scrollbar + smooth wheel scroll for Plotly charts.
 * Attach after Plotly.newPlot; returns cleanup.
 */
import Plotly from "plotly.js-dist-min";

/** Layout margins for dashboard / QAQC-style expanded plot modals */
export function applyExpandedModalPlotLayout(layout, options = {}) {
  if (!layout) {
    return layout;
  }
  const hasLegend = !!options.hasLegend;
  const rightMargin = options.rightMargin ?? (hasLegend ? 140 : 48);
  const titleColor = options.isDarkMode ? "#F9FAFB" : "#333333";

  layout.autosize = true;
  const modalBg = options.isDarkMode ? "transparent" : "#ffffff";
  layout.paper_bgcolor = modalBg;
  layout.plot_bgcolor = modalBg;
  if (options.width && options.width > 200) {
    layout.width = options.width;
  } else {
    delete layout.width;
  }
  if (options.height && options.height > 200) {
    layout.height = options.height;
  }
  layout.margin = {
    l: 72,
    r: rightMargin,
    t: 72,
    b: 96,
    pad: 6,
    autoexpand: true,
    ...(layout.margin || {}),
  };

  const patchAxis = (axisKey) => {
    if (!layout[axisKey]) {
      return;
    }
    const axis = layout[axisKey];
    axis.automargin = true;
    if (axis.title && typeof axis.title === "object") {
      axis.title = {
        ...axis.title,
        standoff: Math.max(axis.title.standoff || 0, 14),
        font: {
          size: 14,
          color: titleColor,
          ...(axis.title.font || {}),
        },
      };
    }
    axis.tickfont = {
      size: 11,
      color: titleColor,
      ...(axis.tickfont || {}),
    };
  };

  patchAxis("xaxis");
  patchAxis("yaxis");

  if (layout.scene) {
    ["xaxis", "yaxis", "zaxis"].forEach((k) => {
      if (layout.scene[k]) {
        layout.scene[k].automargin = true;
      }
    });
  }

  return layout;
}

/** Size modal plot host and return pixel dimensions for Plotly layout */
export function prepareExpandedModalPlotDimensions(chartDiv, heightPx) {
  const overlay = chartDiv?.closest?.(".v-overlay__content");
  const cardText = chartDiv?.closest?.(".dashboard-expanded-card-text");
  const header = cardText?.querySelector?.(".dashboard-expanded-popup-top, .popup-top");
  const headerH = header?.offsetHeight || 72;
  const height =
    heightPx ||
    Math.max(450, (overlay?.clientHeight || Math.floor(window.innerHeight * 0.7)) - headerH - 24);

  chartDiv.style.width = "100%";
  chartDiv.style.maxWidth = "100%";
  chartDiv.style.height = `${height}px`;
  chartDiv.style.minHeight = `${height}px`;
  chartDiv.style.display = "block";
  chartDiv.style.boxSizing = "border-box";

  let width = chartDiv.clientWidth;
  if (!width || width < 200) {
    const overlayW = overlay?.clientWidth || 0;
    width = overlayW > 200 ? overlayW - 32 : Math.floor(window.innerWidth * 0.65);
  }

  return {
    width: Math.max(640, Math.floor(width)),
    height: Math.floor(height),
  };
}

export function finishExpandedModalPlotResize(chartDiv, width, height) {
  if (!chartDiv || !width || !height) {
    return;
  }
  const run = () => {
    Plotly.relayout(chartDiv, { width, height, autosize: false }).catch(() => {});
    Plotly.Plots.resize(chartDiv).catch(() => {});
  };
  run();
  requestAnimationFrame(run);
  setTimeout(run, 150);
}

/** Measure expanded modal chart area after the dialog is visible */
export function getExpandedModalContainerSize(chartDiv) {
  const fallbackWidth = Math.floor(window.innerWidth * 0.85);
  const fallbackHeight = Math.max(480, Math.floor(window.innerHeight * 0.62));

  if (!chartDiv) {
    return { width: fallbackWidth, height: fallbackHeight };
  }

  const overlay = chartDiv.closest?.(".v-overlay__content");
  const overlayWidth = overlay?.clientWidth || 0;
  const overlayHeight = overlay?.clientHeight || 0;

  const width =
    overlayWidth > 200
      ? overlayWidth - 32
      : Math.max(chartDiv.clientWidth || 0, fallbackWidth);

  const height =
    overlayHeight > 200
      ? Math.max(480, overlayHeight - 100)
      : Math.max(chartDiv.clientHeight || 0, fallbackHeight);

  return { width, height };
}

export function estimateLegendRightMargin(tableData, groupColumn, min = 120, max = 400) {
  if (!groupColumn || !Array.isArray(tableData) || !tableData.length) {
    return min;
  }
  const seen = new Set();
  let maxLen = 0;
  for (let i = 0; i < tableData.length; i++) {
    const raw = tableData[i][groupColumn];
    if (raw === undefined || raw === null || raw === "") {
      continue;
    }
    const s = String(raw);
    if (seen.has(s)) {
      continue;
    }
    seen.add(s);
    maxLen = Math.max(maxLen, s.length);
  }
  return Math.min(max, Math.max(min, Math.round(56 + maxLen * 6.2)));
}

/** Plotly relayout patch: legend on the right (Studio / DynamicChart). */
export function getRightLegendRelayoutPatch(tableData, groupColumn, leftMargin = 56) {
  const hasLegend = !!(groupColumn && String(groupColumn).trim());
  const rightMargin = hasLegend
    ? estimateLegendRightMargin(tableData, groupColumn, 120, 400)
    : 28;
  return {
    autosize: true,
    "margin.l": leftMargin,
    "margin.r": rightMargin,
    "legend.xref": "paper",
    "legend.yref": "paper",
    "legend.x": 1,
    "legend.xanchor": "left",
    "legend.y": 0.5,
    "legend.yanchor": "middle",
    "legend.orientation": "v",
  };
}

export function applyRightSideLegend(chartDiv, tableData, groupColumn) {
  const gd = getPlotRoot(chartDiv);
  if (!gd) {
    return Promise.resolve();
  }
  return Plotly.relayout(gd, getRightLegendRelayoutPatch(tableData, groupColumn)).catch(() => {});
}

/** Agentic dashboard: relayout legend to the right (layout only — do not mutate g.legend transform). */
export function fixAgenticDashboardLegend(chartDiv, tableData, groupColumn) {
  return applyRightSideLegend(chartDiv, tableData, groupColumn);
}

const SCROLLER_CLASS = "fd-plot-legend-scroller";
const TRACK_CLASS = "fd-plot-legend-scroller-track";
const THUMB_CLASS = "fd-plot-legend-scroller-thumb";

function getPlotRoot(chartDiv) {
  if (!chartDiv) return null;
  return chartDiv.classList?.contains("js-plotly-plot")
    ? chartDiv
    : chartDiv.querySelector?.(".js-plotly-plot");
}

function getScrollHost(chartDiv) {
  const host =
    chartDiv.closest?.(".chart-container") ||
    chartDiv.closest?.(".main-chart") ||
    chartDiv.closest?.(".chart-card-content") ||
    chartDiv.closest?.(".modal-chart-container") ||
    chartDiv.parentElement ||
    chartDiv;

  if (host && getComputedStyle(host).position === "static") {
    host.style.position = "relative";
  }
  return host;
}

function countLegendItems(plotRoot) {
  if (!plotRoot) return 0;
  const toggles = plotRoot.querySelectorAll(".legend .legendtoggle");
  if (toggles.length) return toggles.length;
  const texts = plotRoot.querySelectorAll(".legendtext");
  return texts.length;
}

function measureLegendOverflow(plotRoot, maxHeightRatio) {
  const plotH = plotRoot.getBoundingClientRect().height || 400;
  const maxH = Math.max(72, plotH * maxHeightRatio);
  const scrollbox = plotRoot.querySelector(".legendscrollbox");

  if (scrollbox) {
    try {
      const sbBox = scrollbox.getBBox();
      const maxScroll = Math.max(0, sbBox.height - maxH);
      return { scrollbox, maxScroll, maxH, itemCount: countLegendItems(plotRoot) };
    } catch {
      void 0;
    }
  }

  const legend = plotRoot.querySelector(".legend");
  if (legend) {
    try {
      const legBox = legend.getBBox();
      const maxScroll = Math.max(0, legBox.height - maxH);
      return {
        scrollbox: scrollbox || null,
        maxScroll,
        maxH,
        itemCount: countLegendItems(plotRoot),
      };
    } catch {
      void 0;
    }
  }

  const itemCount = countLegendItems(plotRoot);
  return { scrollbox: null, maxScroll: itemCount > 6 ? itemCount * 18 : 0, maxH, itemCount };
}

/**
 * Only scroll the inner .legendscrollbox — never g.legend (Plotly uses matrix/translate there).
 */
function applyLegendOffset(scrollTarget, offset) {
  if (!scrollTarget?.classList?.contains("legendscrollbox")) {
    return;
  }
  scrollTarget.setAttribute("transform", `translate(0, ${-offset})`);
}

function ensureScrollerDom(host) {
  let scroller = host.querySelector(`:scope > .${SCROLLER_CLASS}`);
  if (scroller) return scroller;

  scroller = document.createElement("div");
  scroller.className = SCROLLER_CLASS;

  const track = document.createElement("div");
  track.className = TRACK_CLASS;

  const thumb = document.createElement("div");
  thumb.className = THUMB_CLASS;

  track.appendChild(thumb);
  scroller.appendChild(track);
  host.appendChild(scroller);

  return scroller;
}

function positionScroller(host, plotRoot, scroller) {
  const legend = plotRoot.querySelector(".legend");
  if (!legend || !scroller) {
    return false;
  }

  const hostRect = host.getBoundingClientRect();
  const legendRect = legend.getBoundingClientRect();

  if (legendRect.width < 4 || legendRect.height < 4) {
    return false;
  }

  const top = Math.max(0, legendRect.top - hostRect.top);
  const height = Math.min(legendRect.height, hostRect.height - top);
  const rightInset = Math.max(2, hostRect.right - legendRect.right);

  scroller.style.top = `${top}px`;
  scroller.style.height = `${Math.max(48, height)}px`;
  scroller.style.right = `${Math.max(0, rightInset - 2)}px`;

  return true;
}

function updateThumb(scroller, offset, maxScroll) {
  const track = scroller.querySelector(`.${TRACK_CLASS}`);
  const thumb = scroller.querySelector(`.${THUMB_CLASS}`);
  if (!track || !thumb) return;

  const trackH = track.clientHeight || 1;
  if (maxScroll <= 1) {
    thumb.style.height = `${trackH}px`;
    thumb.style.transform = "translateY(0)";
    return;
  }

  const ratio = trackH / (trackH + maxScroll);
  const thumbH = Math.max(20, Math.floor(trackH * ratio));
  const maxThumbTop = Math.max(0, trackH - thumbH);
  const thumbTop = (offset / maxScroll) * maxThumbTop;

  thumb.style.height = `${thumbH}px`;
  thumb.style.transform = `translateY(${thumbTop}px)`;
}

function showPlotlyNativeScrollbar(plotRoot) {
  const nativeBar = plotRoot.querySelector(".scrollbar");
  if (nativeBar) {
    nativeBar.style.display = "";
    nativeBar.style.visibility = "visible";
    nativeBar.style.opacity = "1";
  }
}

export function attachPlotlyLegendWheelScroll(chartDiv, options = {}) {
  if (!chartDiv) {
    return () => {};
  }

  if (typeof chartDiv._fdLegendScrollCleanup === "function") {
    chartDiv._fdLegendScrollCleanup();
  }

  const maxHeightRatio = options.maxHeightRatio ?? 0.78;
  const host = getScrollHost(chartDiv);

  let scrollOffset = 0;
  let maxScroll = 0;
  let scrollbox = null;
  let scrollerEl = null;
  let wheelHandler = null;
  let boundLegend = null;
  let dragging = false;
  let dragStartY = 0;
  let dragStartOffset = 0;
  let resizeObserver = null;

  const setOffset = (next) => {
    scrollOffset = Math.min(maxScroll, Math.max(0, next));
    applyLegendOffset(scrollbox, scrollOffset);
    if (scrollerEl) {
      updateThumb(scrollerEl, scrollOffset, maxScroll);
    }
  };

  const hideScroller = () => {
    if (scrollerEl) {
      scrollerEl.style.display = "none";
    }
  };

  const showScroller = () => {
    if (scrollerEl) {
      scrollerEl.style.display = "block";
    }
  };

  const detachWheel = () => {
    if (wheelHandler && boundLegend) {
      boundLegend.removeEventListener("wheel", wheelHandler);
    }
    wheelHandler = null;
    boundLegend = null;
  };

  const bindThumbDrag = () => {
    if (!scrollerEl) return;
    const thumb = scrollerEl.querySelector(`.${THUMB_CLASS}`);
    const track = scrollerEl.querySelector(`.${TRACK_CLASS}`);
    if (!thumb || !track || thumb._fdBound) return;
    thumb._fdBound = true;

    const onPointerDown = (e) => {
      dragging = true;
      dragStartY = e.clientY;
      dragStartOffset = scrollOffset;
      thumb.setPointerCapture?.(e.pointerId);
      e.preventDefault();
    };

    const onPointerMove = (e) => {
      if (!dragging) return;
      const trackH = track.clientHeight || 1;
      const thumbH = thumb.clientHeight || 20;
      const maxThumbTop = Math.max(1, trackH - thumbH);
      const dy = e.clientY - dragStartY;
      const offsetDelta = (dy / maxThumbTop) * maxScroll;
      setOffset(dragStartOffset + offsetDelta);
    };

    const onPointerUp = () => {
      dragging = false;
    };

    thumb.addEventListener("pointerdown", onPointerDown);
    thumb.addEventListener("pointermove", onPointerMove);
    thumb.addEventListener("pointerup", onPointerUp);
    thumb.addEventListener("pointercancel", onPointerUp);

    track.addEventListener("click", (e) => {
      if (e.target === thumb) return;
      const rect = track.getBoundingClientRect();
      const ratio = (e.clientY - rect.top) / rect.height;
      setOffset(ratio * maxScroll);
    });
  };

  const setup = () => {
    detachWheel();

    const plotRoot = getPlotRoot(chartDiv);
    if (!plotRoot) {
      hideScroller();
      return;
    }

    const legend = plotRoot.querySelector(".legend");
    if (!legend) {
      hideScroller();
      return;
    }

    showPlotlyNativeScrollbar(plotRoot);

    const measured = measureLegendOverflow(plotRoot, maxHeightRatio);
    scrollbox = measured.scrollbox;
    maxScroll = measured.maxScroll;

    const needsScroll = maxScroll > 1 && !!scrollbox;

    if (!needsScroll) {
      scrollOffset = 0;
      if (scrollbox) {
        applyLegendOffset(scrollbox, 0);
      }
      hideScroller();
      return;
    }

    scrollOffset = Math.min(scrollOffset, maxScroll);
    applyLegendOffset(scrollbox, scrollOffset);

    scrollerEl = ensureScrollerDom(host);
    if (!positionScroller(host, plotRoot, scrollerEl)) {
      hideScroller();
      return;
    }

    showScroller();
    updateThumb(scrollerEl, scrollOffset, maxScroll);
    bindThumbDrag();

    wheelHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      setOffset(scrollOffset + delta * 0.4);
    };

    boundLegend = legend;
    legend.addEventListener("wheel", wheelHandler, { passive: false });
    scrollerEl.addEventListener("wheel", wheelHandler, { passive: false });
  };

  const scheduleSetup = () => {
    requestAnimationFrame(() => {
      setup();
      window.setTimeout(setup, 60);
      window.setTimeout(setup, 250);
    });
  };

  const onAfterPlot = () => scheduleSetup();
  const onRelayout = () => scheduleSetup();

  if (typeof chartDiv.on === "function") {
    chartDiv.on("plotly_afterplot", onAfterPlot);
    chartDiv.on("plotly_relayout", onRelayout);
  }

  if (typeof ResizeObserver !== "undefined" && host) {
    resizeObserver = new ResizeObserver(() => scheduleSetup());
    resizeObserver.observe(host);
  }

  scheduleSetup();

  const cleanup = () => {
    detachWheel();
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (typeof chartDiv.removeListener === "function") {
      try {
        chartDiv.removeListener("plotly_afterplot", onAfterPlot);
        chartDiv.removeListener("plotly_relayout", onRelayout);
      } catch {
        void 0;
      }
    }
    if (scrollerEl?.parentNode) {
      scrollerEl.parentNode.removeChild(scrollerEl);
    }
    scrollerEl = null;
    scrollbox = null;
    delete chartDiv._fdLegendScrollCleanup;
  };

  chartDiv._fdLegendScrollCleanup = cleanup;
  return cleanup;
}
